/**
 * `QuoteHighlightExtension` — Tiptap extension that surfaces the
 * "scroll-to-and-orange-glow this quoted text" feature for cross-surface
 * navigation arrivals (search result click, chat citation, etc.).
 *
 * Why this lives as a separate file:
 *
 * 2026-05-18 — the previous MDXEditor implementation tried to wrap the
 * quoted text in a `<mark>` via direct DOM `Range.surroundContents`. That
 * mutated the contentEditable Lexical owns, which broke Lexical's
 * reconciliation cycle and silently produced empty Lexical docs (the
 * #113 error class). The feature was disabled in commit 4468ed4 pending
 * a "do this inside the editor's reconciliation cycle" implementation.
 *
 * Tiptap (ProseMirror) supports this cleanly via **decorations** —
 * ephemeral visual overlays that the editor view renders ON TOP of the
 * document without ever mutating the document itself. No DOM mutation
 * outside the editor's control, no reconciliation conflict, perfectly
 * native to ProseMirror's model.
 *
 * How it works:
 *
 * 1. A ProseMirror Plugin holds a DecorationSet in its state. Empty by
 *    default. When a `setMeta(pluginKey, {from, to})` transaction lands,
 *    the state replaces its DecorationSet with a single inline
 *    Decoration spanning (from, to) and applying the
 *    `.highlight-pulse-orange` class.
 * 2. A `setMeta(pluginKey, null)` transaction clears the DecorationSet
 *    back to empty.
 * 3. Two commands expose this to React-side callers:
 *      - `setQuoteHighlight(text)` — scans the doc for `text`, computes
 *        the (from, to) range, dispatches the meta + scrolls the matching
 *        DOM into view.
 *      - `clearQuoteHighlight()` — dispatches null meta.
 *
 * Text matching is best-effort:
 *
 * - Case-insensitive substring match within a single text node.
 * - Whitespace is normalised (runs of whitespace collapsed to one space
 *   on both haystack and needle) so a snippet pulled from a multi-line
 *   paragraph still matches.
 * - If the quoted text spans MULTIPLE text nodes (because of inline
 *   formatting — bold, link, etc. — splitting the paragraph), we don't
 *   currently match. Acceptable miss rate; the fallback is "no highlight,
 *   just navigated" which is the pre-implementation status quo.
 *
 * The CSS class `.highlight-pulse-orange` (defined in app.css) provides a
 * 10-second incandescent-orange keyframe animation. The decoration sits
 * in the doc for the same duration — the React-side caller is responsible
 * for clearing it.
 */

import { Extension, type Editor } from '@tiptap/react';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet, type EditorView } from '@tiptap/pm/view';
import type { Node as PmNode } from '@tiptap/pm/model';

/** Plugin key — exported so callers can read the plugin state if needed. */
export const quoteHighlightPluginKey = new PluginKey<DecorationSet>(
  'quoteHighlightPlugin',
);

interface SetMeta {
  from: number;
  to: number;
}

/** Module augmentation so `editor.commands.setQuoteHighlight(…)` type-checks. */
declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    quoteHighlight: {
      /**
       * Highlight the first occurrence of `text` in the document with a
       * `.highlight-pulse-orange` decoration and scroll its DOM into view.
       * Returns true if the text was found + highlighted; false if not.
       */
      setQuoteHighlight: (text: string) => ReturnType;
      /** Clear any active highlight decoration. */
      clearQuoteHighlight: () => ReturnType;
    };
  }
}

export const QuoteHighlightExtension = Extension.create({
  name: 'quoteHighlight',

  addProseMirrorPlugins() {
    return [
      new Plugin<DecorationSet>({
        key: quoteHighlightPluginKey,
        state: {
          init: () => DecorationSet.empty,
          apply(tr, oldSet) {
            // `null` meta = explicit clear command. `undefined` = no meta on
            // this transaction (could be a content edit, cursor move, etc.).
            // A defined-non-null meta is a fresh highlight to apply.
            const meta = tr.getMeta(quoteHighlightPluginKey) as
              | SetMeta
              | null
              | undefined;
            if (meta === null) return DecorationSet.empty;
            if (meta !== undefined) {
              return DecorationSet.create(tr.doc, [
                Decoration.inline(meta.from, meta.to, {
                  class: 'highlight-pulse-orange',
                  // Tag the DOM so downstream code (or QA tooling) can find
                  // the highlight via document.querySelector.
                  'data-quote-highlight': 'true',
                }),
              ]);
            }
            // No meta — map the existing set through the transaction so
            // document edits don't invalidate the decoration's positions.
            return oldSet.map(tr.mapping, tr.doc);
          },
        },
        props: {
          decorations(state) {
            return quoteHighlightPluginKey.getState(state);
          },
        },
      }),
    ];
  },

  addCommands() {
    return {
      setQuoteHighlight:
        (text: string) =>
        ({ tr, dispatch, view }) => {
          const range = findTextRangeInDoc(tr.doc, text);
          // eslint-disable-next-line no-console
          console.log('[hl] setQuoteHighlight.cmd', {
            text: text.slice(0, 80),
            docSize: tr.doc.content.size,
            rangeFound: range !== null,
            range,
          });
          if (range === null) return false;
          if (dispatch !== undefined) {
            dispatch(tr.setMeta(quoteHighlightPluginKey, range));
            // Scroll deterministically via ProseMirror's coordsAtPos —
            // doesn't depend on the decoration's DOM rendering having
            // landed (querySelector + rAF could race the decoration's
            // first paint). Walks up the ancestor chain to find the
            // scrollable container, then scrolls it so the match is
            // centred vertically.
            if (view !== undefined) {
              // Defer one tick so the dispatched transaction has been
              // applied to the view's state — coordsAtPos reads from the
              // current state.
              setTimeout(() => {
                scrollEditorToDocPos(view, range.from);
              }, 0);
            }
          }
          return true;
        },
      clearQuoteHighlight:
        () =>
        ({ tr, dispatch }) => {
          if (dispatch !== undefined) {
            dispatch(tr.setMeta(quoteHighlightPluginKey, null));
          }
          return true;
        },
    };
  },
});

/**
 * Find the first occurrence of `needle` inside the document, returning the
 * ProseMirror (from, to) document positions for the match. Returns null on
 * no match.
 *
 * Matching strategy (2026-05-18, revised for multi-text-node support):
 *
 *   1. Walk every text node in the doc, building a single concatenated
 *      "flattened text" string of the entire document. As we accumulate,
 *      we remember which concatenated-string character maps to which
 *      ProseMirror doc position. This lets us find a needle that spans
 *      multiple text nodes (e.g. across a `<strong>` boundary in the
 *      rendered doc — without this, a snippet like "the bold word" wouldn't
 *      match in a doc that renders the second word as `<strong>bold</strong>`).
 *   2. Normalise BOTH sides — lowercase + whitespace runs collapsed to one
 *      space. The position map for the haystack tracks the original char's
 *      doc position so we can map a match's start index back to a real
 *      ProseMirror position.
 *   3. Substring search the normalised haystack for the normalised needle.
 *   4. If found, map start/end indices back to ProseMirror doc positions.
 *
 * Case-insensitive. Whitespace tolerant. Stops walking once a match is
 * found (no need to find ALL occurrences — we only highlight the first).
 */
function findTextRangeInDoc(
  doc: PmNode,
  needle: string,
): { from: number; to: number } | null {
  // CRITICAL: the search snippet comes in as RAW MARKDOWN from the backend
  // (ts_headline excerpt of the canonical body, which is stored as markdown).
  // The Tiptap doc has the PARSED form — `###` is a heading node, `**bold**`
  // is text + bold mark, `- ` is a list item, etc. The text content we walk
  // via `doc.descendants` is just the plain text within those nodes — no
  // markdown syntax. So a needle like `### Decisions\n\n- **Service-unit...`
  // can never match the text-node content `Decisions Service-unit...`.
  //
  // Fix: strip markdown syntax from the needle, leaving just the text
  // content (the same shape we'll be comparing against). Plus the
  // ts_headline ellipsis markers Postgres adds at fragment boundaries.
  const cleaned = stripMarkdownSyntax(needle)
    .replace(/[…]/g, ' ')
    .replace(/\.{3,}/g, ' ')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();
  if (cleaned.length === 0) return null;
  const tryMatch = (n: string): { from: number; to: number } | null => {
    if (n.length < 8) return null; // too short → too many false positives
    return findExactInDoc(doc, n);
  };
  // Try progressively shorter prefixes: 100% → 60% → 30%. Snippet edges
  // sometimes contain partial words / mid-formatting that don't quite line
  // up with text-node boundaries; a shorter prefix lands cleanly on the
  // first complete phrase.
  const lengths = [cleaned.length, Math.floor(cleaned.length * 0.6), Math.floor(cleaned.length * 0.3)];
  for (const len of lengths) {
    const r = tryMatch(cleaned.slice(0, len));
    if (r !== null) return r;
  }
  return null;
}

/**
 * Strip markdown syntax characters from a string, leaving the underlying
 * text content. Targeted — only the constructs that produce structurally
 * distinct text-node content in Tiptap. Order matters: code blocks first
 * (they swallow other syntax), then inline patterns.
 */
function stripMarkdownSyntax(text: string): string {
  return text
    .replace(/```[\s\S]*?```/g, ' ') // fenced code blocks
    .replace(/^\s*#{1,6}\s+/gm, '') // ATX headers
    .replace(/^\s*>\s+/gm, '') // blockquote markers
    .replace(/^\s*[-*+]\s+/gm, '') // bullet markers
    .replace(/^\s*\d+\.\s+/gm, '') // numbered list markers
    .replace(/!\[[^\]]*\]\([^)]+\)/g, '') // images (drop entirely)
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // links (keep text)
    .replace(/`([^`]+)`/g, '$1') // inline code
    .replace(/\*\*([^*]+)\*\*/g, '$1') // **bold**
    .replace(/__([^_]+)__/g, '$1') // __bold__
    .replace(/\*([^*]+)\*/g, '$1') // *italic*
    .replace(/_([^_]+)_/g, '$1') // _italic_
    .replace(/~~([^~]+)~~/g, '$1') // ~~strike~~
    .replace(/<[^>]+>/g, ''); // raw HTML tags
}

function findExactInDoc(
  doc: PmNode,
  normalisedNeedle: string,
): { from: number; to: number } | null {
  if (normalisedNeedle.length === 0) return null;

  // Pass 1: build the flattened normalised text + a char→doc-pos map.
  let flat = '';
  // For each character in `flat`, the corresponding doc position. Same
  // length as `flat`. (A doc position points BEFORE the char; to mark the
  // range AFTER a char, use the doc pos of the NEXT char, or extrapolate by
  // +1 for the final char of the match.)
  const posMap: number[] = [];
  let lastEmittedWasSpace = false;

  doc.descendants((node, pos) => {
    if (!node.isText) return true;
    const raw = node.text ?? '';
    for (let i = 0; i < raw.length; i++) {
      const ch = raw[i];
      const docPos = pos + i;
      if (/\s/.test(ch)) {
        if (lastEmittedWasSpace) continue; // collapse runs of whitespace
        flat += ' ';
        posMap.push(docPos);
        lastEmittedWasSpace = true;
      } else {
        flat += ch.toLowerCase();
        posMap.push(docPos);
        lastEmittedWasSpace = false;
      }
    }
    // A block boundary between text nodes acts like whitespace — emit a
    // single space if we didn't just emit one. This lets a needle that
    // crosses two paragraphs still match if it doesn't care about the
    // paragraph break.
    if (!lastEmittedWasSpace) {
      flat += ' ';
      posMap.push(pos + raw.length);
      lastEmittedWasSpace = true;
    }
    return true;
  });

  const matchStart = flat.indexOf(normalisedNeedle);
  if (matchStart < 0) return null;
  const matchEndExclusive = matchStart + normalisedNeedle.length;
  // Guard against off-by-one when the match runs to the very end of the
  // doc — use the doc pos of the last matched char and add 1.
  const fromDocPos = posMap[matchStart];
  const toDocPos =
    matchEndExclusive < posMap.length
      ? posMap[matchEndExclusive]
      : posMap[posMap.length - 1] + 1;
  if (fromDocPos === undefined || toDocPos === undefined) return null;
  if (toDocPos <= fromDocPos) return null;
  return { from: fromDocPos, to: toDocPos };
}

/**
 * Helper for callers that hold an `Editor` instance — calls the command
 * for them. Mostly for readability at the call site.
 */
export function highlightQuoteInEditor(editor: Editor, text: string): boolean {
  return editor.commands.setQuoteHighlight(text);
}

/**
 * Walk up the editor view's DOM ancestor chain to find the first scrollable
 * container, then scroll it so the doc position `pos` is roughly centred
 * vertically. Uses ProseMirror's `coordsAtPos` so it doesn't depend on the
 * decoration's DOM rendering having landed yet — important because the
 * scroll fires immediately after the decoration-setting transaction and
 * the browser may not have painted yet.
 *
 * Silently no-ops on failures (no scrollable parent, coordsAtPos throws on
 * an invalid pos, etc.). The fallback is "user is on the right note but
 * has to find the highlight themselves" — same as before this feature
 * existed.
 */
function scrollEditorToDocPos(view: EditorView, pos: number): void {
  let coords: { top: number; left: number; bottom: number; right: number };
  try {
    coords = view.coordsAtPos(pos);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.log('[hl] scroll.coordsAtPos.threw', e);
    return;
  }
  const scrollParent = findScrollableAncestor(view.dom);
  // eslint-disable-next-line no-console
  console.log('[hl] scroll', {
    pos,
    coords,
    scrollParent: scrollParent?.className ?? '(none)',
    scrollParentScrollTop: scrollParent?.scrollTop,
    scrollParentClientHeight: scrollParent?.clientHeight,
  });
  if (scrollParent === null) return;
  const containerRect = scrollParent.getBoundingClientRect();
  const offsetWithinContainer = coords.top - containerRect.top;
  const desiredTop =
    scrollParent.scrollTop +
    offsetWithinContainer -
    scrollParent.clientHeight / 2;
  // eslint-disable-next-line no-console
  console.log('[hl] scroll.target', {
    offsetWithinContainer,
    desiredTop: Math.max(0, desiredTop),
  });
  scrollParent.scrollTo({
    top: Math.max(0, desiredTop),
    behavior: 'smooth',
  });
}

/**
 * Walk up from `el` until we find an ancestor whose computed `overflow-y`
 * is `auto` or `scroll` — that's the scrolling container. Returns null if
 * none found (we hit the document root).
 */
function findScrollableAncestor(el: HTMLElement): HTMLElement | null {
  let current: HTMLElement | null = el.parentElement;
  while (current !== null) {
    const style = window.getComputedStyle(current);
    if (style.overflowY === 'auto' || style.overflowY === 'scroll') {
      return current;
    }
    current = current.parentElement;
  }
  return null;
}
